import type { NormRect } from './fields.js';

/**
 * Alignment, distribution and snapping.
 *
 * Pure functions over rectangles, deliberately separate from the drag handlers.
 * This is fiddly geometry that is easy to get subtly wrong — off-by-one-edge
 * bugs in an align tool are the kind of thing you only notice after placing
 * forty fields — so it lives here where it can be tested exhaustively.
 */

export type AlignMode = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';
export type DistributeMode = 'horizontal' | 'vertical';

export interface Bounds { minX: number; minY: number; maxX: number; maxY: number }

export function boundsOf(rects: NormRect[]): Bounds {
  return {
    minX: Math.min(...rects.map((r) => r.x)),
    minY: Math.min(...rects.map((r) => r.y)),
    maxX: Math.max(...rects.map((r) => r.x + r.width)),
    maxY: Math.max(...rects.map((r) => r.y + r.height)),
  };
}

/**
 * Aligns rects to the bounding box of the selection.
 *
 * Aligning to the selection bounds rather than to a "key object" is the
 * behaviour most people expect from a first pass and needs no extra UI to pick
 * an anchor. Fewer than two rects is a no-op — aligning one item to itself
 * would silently move it to the page edge, which is never what was meant.
 */
export function align<T extends NormRect>(rects: T[], mode: AlignMode): T[] {
  if (rects.length < 2) return rects;
  const b = boundsOf(rects);
  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;

  return rects.map((r) => {
    switch (mode) {
      case 'left':    return { ...r, x: b.minX };
      case 'right':   return { ...r, x: b.maxX - r.width };
      case 'hcenter': return { ...r, x: cx - r.width / 2 };
      case 'top':     return { ...r, y: b.minY };
      case 'bottom':  return { ...r, y: b.maxY - r.height };
      case 'vcenter': return { ...r, y: cy - r.height / 2 };
    }
  });
}

/**
 * Distributes rects so the GAPS between them are equal.
 *
 * Equal gaps, not equal centres. With mixed-width fields — a checkbox next to a
 * signature block — equal centre spacing looks visibly wrong because the gaps
 * differ. Equal gaps is what "distribute" means to the eye.
 *
 * The outermost two rects never move; everything between them is repositioned.
 */
export function distribute<T extends NormRect>(rects: T[], mode: DistributeMode): T[] {
  if (rects.length < 3) return rects;
  const horiz = mode === 'horizontal';
  const pos = (r: NormRect) => (horiz ? r.x : r.y);
  const size = (r: NormRect) => (horiz ? r.width : r.height);

  const order = [...rects].sort((a, b) => pos(a) - pos(b));
  const first = order[0]!;
  const last = order[order.length - 1]!;

  const span = pos(last) + size(last) - pos(first);
  const totalSize = order.reduce((n, r) => n + size(r), 0);
  const gap = (span - totalSize) / (order.length - 1);

  const placed = new Map<T, number>();
  let cursor = pos(first);
  for (const r of order) {
    placed.set(r, cursor);
    cursor += size(r) + gap;
  }

  return rects.map((r) => {
    const p = placed.get(r);
    if (p === undefined) return r;
    return horiz ? { ...r, x: p } : { ...r, y: p };
  });
}

export interface SnapResult { rect: NormRect; guides: SnapGuide[] }
export interface SnapGuide { axis: 'x' | 'y'; at: number }

/**
 * Snaps a moving rect to nearby edges and centres of static rects.
 *
 * `threshold` is in normalized units; the caller converts from pixels so the
 * snap feels the same at every zoom level. Without that conversion snapping is
 * hair-trigger when zoomed in and unusable when zoomed out.
 */
export function snap(
  moving: NormRect,
  others: NormRect[],
  threshold: number
): SnapResult {
  const guides: SnapGuide[] = [];
  let { x, y } = moving;

  const movX = [moving.x, moving.x + moving.width / 2, moving.x + moving.width];
  const movY = [moving.y, moving.y + moving.height / 2, moving.y + moving.height];
  const offX = [0, -moving.width / 2, -moving.width];
  const offY = [0, -moving.height / 2, -moving.height];

  let bestX = threshold;
  let bestY = threshold;

  for (const o of others) {
    const targX = [o.x, o.x + o.width / 2, o.x + o.width];
    const targY = [o.y, o.y + o.height / 2, o.y + o.height];

    for (let i = 0; i < 3; i++) {
      for (const t of targX) {
        const d = Math.abs(movX[i]! - t);
        if (d < bestX) { bestX = d; x = t + offX[i]!; guides.push({ axis: 'x', at: t }); }
      }
      for (const t of targY) {
        const d = Math.abs(movY[i]! - t);
        if (d < bestY) { bestY = d; y = t + offY[i]!; guides.push({ axis: 'y', at: t }); }
      }
    }
  }

  return { rect: { ...moving, x, y }, guides };
}
